<template>
  <RevLoadingScreen v-if="pending" :text="i18n(translations.fetchingData)" />
  <div v-else>
    <div class="body-1-bold">
      {{ i18n(translations.similarListingForProduct) }}
    </div>
    <div v-if="similarListings?.length">
      <RevTable :collection="similarListings" :columns transparent-header>
        <template #body-aestheticGrade="{ item }">
          <RevTag :label="String(item.aestheticGrade)" variant="secondary" />
        </template>
        <template #body-actions="{ item }">
          <RevButton
            size="small"
            variant="secondary"
            @click="handleSeeListing(String(item.sku))"
          >
            {{ i18n(translations.viewButton) }}
          </RevButton>
        </template>
      </RevTable>
    </div>
    <div v-else>
      {{ i18n(translations.noResults) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'

import { merchantsAPI } from '@backmarket/http-api'
import type { Listing } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevTable } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'

import { ROUTE_NAMES } from '@/constants/routes'
import { useListingPagePreference } from '~/scopes/listings/pages/listingsV2/composables/useListingPagePreference'

import translations from './SimilarListings.translations'

const { isListingsPageV2Default } = useListingPagePreference()

const i18n = useI18n()
const router = useRouter()

const props = defineProps<{
  listing: Listing
}>()

const { data: similarListings, pending } = useHttpFetch(
  merchantsAPI.getSimilarListings,
  {
    pathParams: {
      listingId: props.listing.id,
      productId: props.listing.productId,
    },
    transform: (data) => {
      return (data.listings || []).map((listingInfo) => {
        const formattedListing: Record<string, string | number> = {}

        listingInfo.forEach(([key, value]) => {
          if (key === 'aestheticGrade') {
            formattedListing[key] = value[1][1]
          } else {
            formattedListing[key] = value
          }
        })

        return formattedListing
      })
    },
  },
)

const columns = [
  { key: 'sku', label: i18n(translations.SKU) },
  { key: 'aestheticGrade', label: i18n(translations.appearance) },
  { key: 'quantity', label: i18n(translations.stock) },
  { key: 'actions', label: '' },
]

function handleSeeListing(sku: string) {
  const routeData = router.resolve({
    name: isListingsPageV2Default.value
      ? ROUTE_NAMES.LISTINGSV2.INDEX
      : ROUTE_NAMES.LISTINGS.INDEX,
    query: {
      sku,
    },
  })

  window.open(routeData.href, '_blank')
}
</script>
